import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, CheckCircle2Icon, CircleIcon, HomeIcon, ListTodoIcon, CalendarIcon, UserIcon } from 'lucide-react';
import { webappAppPoints, webappPlatforms } from '../data/webapplicaties';
const NORDEN_RAKET = 'https://cdn.magicpatterns.com/uploads/1WqxCzJ38hFJxSHm98buvx/Norden_met_raket.png';
const EASE = [0.23, 1, 0.32, 1];

// Voorbeeldtaken in de telefoon-mockup. Illustratief, geen klantdata.
const mockTasks = [{
  label: 'Offerte nakijken',
  who: 'JD',
  color: 'bg-emerald-500',
  done: true
}, {
  label: 'Materiaal bestellen',
  who: 'MK',
  color: 'bg-amber-500',
  done: false
}, {
  label: 'Klant terugbellen',
  who: 'SV',
  color: 'bg-violet-500',
  done: false
}, {
  label: 'Planning volgende week',
  who: 'JD',
  color: 'bg-emerald-500',
  done: false
}];

// "Voelt als een echte app": Norden met raket naast een telefoon-mockup.
export const WebappAppExperience: React.FC = () => {
  const reduceMotion = useReducedMotion();
  return <section id="app-ervaring" className="w-full scroll-mt-24 overflow-hidden bg-[#EEF5FA] py-20 md:py-24" data-id="element-855" data-mp="uos71ajigi89">
      <div className="container mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8" data-id="element-856" data-mp="zlilxujkx7s3">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-857" data-mp="kqck7jrinv4k">
          <motion.div className="lg:col-span-6" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-858" data-mp="pnut5a5jaack">
            <h2 className="mb-4 text-3xl font-bold text-[#111827] md:text-4xl" data-id="element-859" data-mp="204i94zjzapy">
              Het voelt als een echte app, niet als een website
            </h2>
            <p className="mb-8 text-lg leading-relaxed text-[#4B5563]" data-id="element-860" data-mp="3p3ringq06ave">
              Je team opent de applicatie zoals ze WhatsApp of hun bankapp
              openen. Een tik op het icoon en je zit erin. Geen inlogpagina van
              een website, maar een bruikbare app voor het dagelijkse werk.
            </p>

            <ul className="mb-10 space-y-4" data-id="element-861" data-mp="1uvfbsxg2h4fb">
              {webappAppPoints.map(point => <li key={point} className="flex items-start gap-3" data-id="element-862" data-mp="1pwu98rdce8np">
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#004AC7] text-white" data-id="element-863" data-mp="1px3xoaiqo7ra">
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-864" data-mp="2il9iex9slwr4" />
                  </span>
                  <span className="leading-relaxed text-[#374151]" data-id="element-865" data-mp="2dmojr2a6vl4s">{point}</span>
                </li>)}
            </ul>

            <p className="mb-4 text-sm font-semibold text-[#111827]" data-id="element-866" data-mp="28mkb1yfivup4">
              Te installeren op
            </p>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-867" data-mp="24kyqrs0nse4w">
              {webappPlatforms.map(platform => {
              const Icon = platform.icon;
              return <li key={platform.label} className="flex flex-col items-center gap-2 rounded-2xl border border-white bg-white px-3 py-4 text-center shadow-sm" data-id="element-868" data-mp="t621ch0kbmx0">
                    <Icon className="h-6 w-6 text-[#004AC7]" aria-hidden="true" data-id="element-869" data-mp="34ru5oz1ydrjg" />
                    <span className="text-sm font-medium text-[#111827]" data-id="element-870" data-mp="38w1sursrizfk">
                      {platform.label}
                    </span>
                  </li>;
            })}
            </ul>
          </motion.div>

          {/* Telefoon + Norden */}
          <div className="relative mx-auto flex w-full max-w-md justify-center lg:col-span-6 lg:justify-end" data-id="element-871" data-mp="3nkoipng0n9l1">
            <div className="relative w-64 rounded-[2.5rem] border-[10px] border-[#1B2B44] bg-white shadow-2xl sm:w-72" role="img" aria-label="Voorbeeld van een webapplicatie op een telefoon met een takenlijst voor het team" data-id="element-872" data-mp="3siq44f0wzkzo">
              <div className="mx-auto mt-2 h-1.5 w-16 rounded-full bg-[#1B2B44]/15" data-id="element-873" data-mp="5difeveqty4t" />
              <div className="mx-3 mt-3 rounded-2xl bg-[#004AC7] px-4 py-4 text-white" data-id="element-874" data-mp="2284zkjtyom94">
                <p className="text-xs text-white/80" data-id="element-875" data-mp="275dabzcs5o0c">Vandaag</p>
                <p className="text-lg font-bold" data-id="element-876" data-mp="277zpak2035sl">Taken van het team</p>
                <div className="mt-3 h-1.5 w-full rounded-full bg-white/25" data-id="element-877" data-mp="1bu5chcj3oh9v">
                  <div className="h-1.5 w-1/4 rounded-full bg-white" data-id="element-878" data-mp="16w3tsspl6jyx" />
                </div>
              </div>
              <ul className="space-y-2 px-3 py-4" data-id="element-879" data-mp="1ltnuvojd3uzt">
                {mockTasks.map(task => <li key={task.label} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#F7F8FA] px-3 py-2.5" data-id="element-880" data-mp="1gutaii5qxydl">
                    {task.done ? <CheckCircle2Icon className="h-5 w-5 flex-shrink-0 text-emerald-600" aria-hidden="true" data-id="element-881" data-mp="t3e3wd6xsuwj" /> : <CircleIcon className="h-5 w-5 flex-shrink-0 text-gray-300" aria-hidden="true" data-id="element-882" data-mp="o4b0syaioyr7" />}
                    <span className={`flex-1 truncate text-sm ${task.done ? 'text-[#6B7280] line-through' : 'font-medium text-[#111827]'}`} data-id="element-883" data-mp="12xelhejensm5">
                      {task.label}
                    </span>
                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${task.color}`} data-id="element-884" data-mp="acwk4y4319nb">
                      {task.who}
                    </span>
                  </li>)}
              </ul>
              <div className="flex items-center justify-around border-t border-gray-100 px-3 pb-4 pt-3 text-[#9CA3AF]" data-id="element-885" data-mp="2lv1bpso8z6hq">
                <HomeIcon className="h-5 w-5" aria-hidden="true" data-id="element-886" data-mp="276og4wt2npnr" />
                <ListTodoIcon className="h-5 w-5 text-[#004AC7]" aria-hidden="true" data-id="element-887" data-mp="2c3evtj0ysj8n" />
                <CalendarIcon className="h-5 w-5" aria-hidden="true" data-id="element-888" data-mp="2zt96btxt9vpq" />
                <UserIcon className="h-5 w-5" aria-hidden="true" data-id="element-889" data-mp="2zry3pxg3qu07" />
              </div>
            </div>

            {/* Norden */}
            <div className="absolute -bottom-6 -left-2 w-36 sm:-left-6 sm:w-44 lg:-left-10" data-id="element-890" data-mp="2qtm3d3pfohuk">
              <div className="absolute -top-12 left-6 whitespace-nowrap rounded-2xl rounded-bl-md bg-white px-4 py-2 text-sm font-semibold text-[#111827] shadow-md sm:left-10" data-id="element-891" data-mp="2uu4x53avzxze">
                Gewoon een app!
              </div>
              <motion.img src={NORDEN_RAKET} alt="Norden, de mascotte van Noord Digital, met een raket" loading="lazy" className="w-full drop-shadow-xl" animate={reduceMotion ? undefined : {
              y: [0, -8, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-892" data-mp="1jgakkwba38of" />
            </div>
          </div>
        </div>
      </div>
    </section>;
};